<script lang="ts">
export default {
  name: 'WholesalerUpdateModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import ConfirmModal from './ConfirmModal.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import { WholesalerAdmin } from '@/types/WholesalerAdmin';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import AppButton from '@/components/v2/AppButton.vue';
import { Edit2 } from '@lucide/vue';

const props = withDefaults(
  defineProps<{
    wholesaler: WholesalerAdmin;
    onSave: (wholesalerId: number, country: string, wholesalerGroup: string) => void;
    countries: string[];
    wholesalerGroups: WholesalerGroup[];
    /** When true, modal is controlled by parent (open on mount, no trigger button). */
    show?: boolean;
  }>(),
  { show: undefined },
);

const emit = defineEmits<{ closed: [] }>();

const selectedCountry = ref('');
const selectedWholesalerGroup = ref('');

const countryOptions = computed<ComboboxOption[]>(() => props.countries.map((c) => ({ label: c, value: c })));
const groupOptions = computed<ComboboxOption[]>(() => props.wholesalerGroups.map((g) => ({ label: g.name, value: g.name })));

const isNewWholesalerGroup = computed<boolean>(() => {
  const name = selectedWholesalerGroup.value.trim();
  return name !== '' && !props.wholesalerGroups.some((g) => g.name === name);
});

async function save(): Promise<boolean> {
  document.body.focus();
  props.onSave(props.wholesaler.id, selectedCountry.value, selectedWholesalerGroup.value);
  return true;
}

function dialogOpened() {
  selectedCountry.value = props.wholesaler.country;
  selectedWholesalerGroup.value = props.wholesaler?.wholesalerGroup ?? '';
}
</script>

<template>
  <ConfirmModal
    :title="`Editing Wholesaler`"
    :subtitle="wholesaler.name"
    :action="save"
    confirmText="Save"
    size="md"
    :show="show"
    @open="dialogOpened"
    @closed="emit('closed')">
    <template
      v-if="show !== true"
      #button="{ props: slotProps }">
      <AppButton
        class="rounded-full"
        v-bind="slotProps">
        <Edit2 :size="16" />
      </AppButton>
    </template>

    <div class="flex flex-col gap-4 p-2">
      <div>
        <label
          for="wholesaler-country"
          class="mb-1 block text-sm font-medium text-gray-700">
          Country
        </label>
        <Combobox
          id="wholesaler-country"
          v-model="selectedCountry"
          :options="countryOptions"
          placeholder="Select country"
          :data-test="`wholesaler-country-${wholesaler.id}`" />
      </div>
      <div>
        <label
          for="wholesaler-group"
          class="mb-1 block text-sm font-medium text-gray-700">
          Wholesaler Group
        </label>
        <Combobox
          id="wholesaler-group"
          v-model="selectedWholesalerGroup"
          :options="groupOptions"
          placeholder="Select wholesaler group"
          allow-free-text
          :data-test="`wholesaler-group-${wholesaler.id}`" />
        <p
          v-if="isNewWholesalerGroup"
          class="mt-1 text-sm text-gray-600">
          A new wholesaler group "{{ selectedWholesalerGroup.trim() }}" will be created and associated to this
          wholesaler.
        </p>
      </div>
    </div>
  </ConfirmModal>
</template>
